import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './Navbar.css';
import logo from '../assets/logo.png';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const handleHamburgerClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    // Mobile detection is now handled by CSS media queries

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Our Services' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/about', label: 'About Us' },
    { path: '/contact', label: 'Contact Us' }
  ];

  return (
    <motion.nav 
      className={`navbar ${isMenuOpen ? 'active' : ''} ${scrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <div className="navbardiv">
        <motion.div 
          className={`logo ${isMenuOpen ? 'mobile-menu-open' : ''}`}
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <Link to="/" onClick={handleLinkClick}>
            <img 
              src={logo} 
              alt="Radha IT Logo" 
              className="navbar-logo"
              style={{ 
                height: '50px', 
                width: 'auto',
                maxWidth: '200px',
                imageRendering: 'crisp-edges',
                objectFit: 'contain'
              }}
            />
          </Link>
        </motion.div>
        
        <motion.button 
          className="hamburger" 
          onClick={handleHamburgerClick}
          whileTap={{ scale: 0.95 }}
          aria-label="Toggle navigation menu"
          aria-expanded={isMenuOpen}
          aria-controls="navigation-menu"
        >
          <motion.div
            animate={isMenuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3 }}
          />
          <motion.div
            animate={isMenuOpen ? { opacity: 0 } : { opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
          <motion.div
            animate={isMenuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3 }}
          />
        </motion.button>
        
        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="mobile-menu-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={handleLinkClick}
            />
          )}
        </AnimatePresence>

        {/* Mobile Menu */}
        <AnimatePresence>
          <motion.div
            className="mobile-menu"
            initial={{ x: "100%" }}
            animate={isMenuOpen ? { x: 0 } : { x: "100%" }}
            exit={{ x: "100%" }}
            transition={{ 
              type: "spring", 
              damping: 25, 
              stiffness: 200 
            }}
          >
            <div className="mobile-menu-header">
              <div className="mobile-menu-logo">
                <img 
                  src={logo} 
                  alt="Radha IT Logo" 
                  className="mobile-logo"
                />
              </div>
              <motion.button 
                className="mobile-menu-close"
                onClick={handleHamburgerClick}
                whileTap={{ scale: 0.95 }}
                aria-label="Close menu"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </motion.button>
            </div>

            <motion.ul 
              id="navigation-menu"
              className="mobile-nav-links"
              role="navigation"
              aria-label="Main navigation"
            >
              {navItems.map((item, index) => (
                <motion.li 
                  key={item.path}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ 
                    delay: index * 0.1,
                    type: "spring",
                    damping: 20,
                    stiffness: 100
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Link 
                    to={item.path} 
                    onClick={handleLinkClick}
                    className={`mobile-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                  >
                    <span className="mobile-nav-text">{item.label}</span>
                    <motion.div 
                      className="mobile-nav-arrow"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 + 0.2 }}
                    >
                      →
                    </motion.div>
                  </Link>
                </motion.li>
              ))}
            </motion.ul>

            <div className="mobile-menu-footer">
              <div className="mobile-contact-info">
                <p className="mobile-contact-text">Ready to get started?</p>
                <Link 
                  to="/contact" 
                  className="mobile-contact-button"
                  onClick={handleLinkClick}
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Desktop Menu */}
        <ul className="desktop-nav-links">
          {navItems.map((item, index) => (
            <motion.li 
              key={item.path}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                to={item.path} 
                onClick={handleLinkClick}
                className={location.pathname === item.path ? 'active' : ''}
              >
                {item.label}
              </Link>
            </motion.li>
          ))}
        </ul>
      </div>
    </motion.nav>
  );

}

export default Navbar;
